import React, { useState, useRef } from 'react';
import './ImageUpload.css';

/**
 * Convierte cualquier imagen seleccionada a File WebP (cliente).
 * El servidor re-codifica igual; esto evita mandar originales JPG/PNG.
 */
async function toWebpFile(file, maxW = 1200, quality = 0.8) {
  if (!file || !file.type.startsWith('image/')) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxW / bitmap.width);
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    if (bitmap.close) bitmap.close();
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/webp', quality)
    );
    if (!blob || blob.type !== 'image/webp') return file;
    const base = (file.name || 'imagen').replace(/\.[^.]+$/, '');
    return new File([blob], `${base}.webp`, { type: 'image/webp', lastModified: Date.now() });
  } catch (_) {
    return file;
  }
}

export default function ImageUpload({ onFileSelect, currentImage, label = "Imagen", name = "foto" }) {
  const [preview, setPreview] = useState(currentImage);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const webp = await toWebpFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(webp);

    onFileSelect(webp);
  };

  const onSelectFile = (e) => {
    handleFile(e.target.files[0]);
    e.target.value = '';
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const onDragLeave = () => {
    setIsDragOver(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  return (
    <div className="img-upload-group">
      <label className="f-lbl">{label}</label>
      <div
        className={`img-upload-area ${isDragOver ? 'drag-over' : ''} ${preview ? 'has-preview' : ''}`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => fileInputRef.current.click()}
      >
        {preview ? (
          <div className="img-preview-container">
            <img src={preview} alt="Vista previa" className="img-preview" />
            <div className="img-overlay">
              <span>Click para cambiar imagen</span>
            </div>
          </div>
        ) : (
          <div className="img-placeholder">
            <div className="img-ico">🖼️</div>
            <div className="img-txt">
              <strong>Haz click o arrastra</strong>
              <span>Se guarda como WebP (Máx 5MB)</span>
            </div>
          </div>
        )}
        <input
          type="file"
          ref={fileInputRef}
          name={name}
          onChange={onSelectFile}
          accept="image/jpeg,image/png,image/webp,image/avif,image/gif"
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
}
